import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Settings2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { getConfigPersentase } from "@/pages/common/remun-dosen/api/get-config-persentase";
import { postConfigPersentase } from "@/pages/common/remun-dosen/api/post-config-persentase";

export function ConfigPersentaseDialog() {
  const [open, setOpen] = useState(false);
  const [bkdPercent, setBkdPercent] = useState<number>(60);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["config-persentase"],
    queryFn: getConfigPersentase,
    enabled: open,
  });

  useEffect(() => {
    if (data?.persentase_bkd !== undefined) {
      setBkdPercent(Math.round(data.persentase_bkd * 100));
    }
  }, [data]);

  const ikuPercent = 100 - bkdPercent;

  const mutation = useMutation({
    mutationFn: postConfigPersentase,
    onSuccess: () => {
      toast.success("Konfigurasi persentase berhasil disimpan");
      queryClient.invalidateQueries({ queryKey: ["config-persentase"] });
      setOpen(false);
    },
    onError: () => {
      toast.error("Gagal menyimpan konfigurasi persentase");
    },
  });

  const handleSave = () => {
    mutation.mutate({
      persentase_bkd: bkdPercent / 100,
      persentase_iku: ikuPercent / 100,
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="font-semibold shadow-sm ml-2">
          <Settings2 className="w-4 h-4 mr-2" />
          Config Persentase
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Konfigurasi Persentase</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {isLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label>Persentase Kinerja Dasar / BKD (%)</Label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={bkdPercent}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 0;
                    setBkdPercent(Math.min(100, Math.max(0, val)));
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label>Persentase IKU (%)</Label>
                <Input
                  type="number"
                  value={ikuPercent}
                  disabled
                  className="bg-muted"
                />
              </div>
              <div className="text-xs text-muted-foreground pt-2">
                Total persentase BKD dan IKU akan otomatis berjumlah 100%.
              </div>
            </>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Batal
          </Button>
          <Button
            onClick={handleSave}
            disabled={mutation.isPending || isLoading}
          >
            {mutation.isPending && (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            )}
            Simpan
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
